/**
 * ProtectedRoute Component
 *
 * Wrapper for routes that require authentication and group membership.
 * Redirects to sign-in if not authenticated.
 * Shows access denied page if authenticated but missing required group.
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated, getUserGroups, hasRequiredGroup, signIn } from '../auth';

const ProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState({
    loading: true,
    authenticated: false,
    hasAccess: false,
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await isAuthenticated();

        if (!authenticated) {
          // Not authenticated - redirect to sign in
          setAuthState({ loading: false, authenticated: false, hasAccess: false });
          return;
        }

        // Check group membership
        const groups = await getUserGroups();
        const hasAccess = hasRequiredGroup(groups);

        setAuthState({
          loading: false,
          authenticated: true,
          hasAccess,
        });
      } catch (error) {
        console.error('Failed to check authentication:', error);
        setAuthState({ loading: false, authenticated: false, hasAccess: false });
      }
    };

    checkAuth();
  }, []);

  // Show loading state while checking authentication
  if (authState.loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '18px', color: '#666' }}>Checking authentication...</div>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to sign in
  if (!authState.authenticated) {
    signIn();
    return null;
  }

  // Authenticated but no access - redirect to access denied page
  if (!authState.hasAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  // Authenticated and has access - render protected content
  return children;
};

export default ProtectedRoute;
